import { useSyncExternalStore } from 'react';
import { isDnd, type QuietHours } from '@nexo/types';
import { isDesktop } from '../../lib/platform';

/** Per-device alert preferences (kept in localStorage, like the theme). */
export interface AlertPrefs {
  sound: boolean;
  desktop: boolean;
  /** `all`: every message; `mentions`: only direct messages, mentions and calls. */
  scope: 'all' | 'mentions';
}

const PREFS_KEY = 'nexo.alerts';
const DEFAULTS: AlertPrefs = { sound: true, desktop: true, scope: 'all' };

function read(): AlertPrefs {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as object) };
  } catch {
    return DEFAULTS;
  }
}

let prefs = read();
const listeners = new Set<() => void>();

export const alertPrefs = {
  get: () => prefs,
  set: (patch: Partial<AlertPrefs>) => {
    prefs = { ...prefs, ...patch };
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    listeners.forEach((l) => l());
  },
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

export const useAlertPrefs = () => useSyncExternalStore(alertPrefs.subscribe, alertPrefs.get);

/* ---------- Do not disturb ---------- */

let dnd: { dndUntil: string | null; quietHours: QuietHours | null } = {
  dndUntil: null,
  quietHours: null,
};

/** Keeps the signed-in user's do-not-disturb settings (set from AuthProvider). */
export function setAlertDnd(next: typeof dnd): void {
  dnd = next;
}

/** Sounds and system notifications stay quiet while do-not-disturb is on. */
export const quietNow = () => isDnd(dnd);

/* ---------- Sounds (synthesised: no audio files to ship) ---------- */

export type AlertSound = 'message' | 'mention' | 'call' | 'join' | 'leave';

let audio: AudioContext | null = null;

/**
 * Short, soft tones in the spirit of Discord's: [frequency Hz, start s, duration s, volume].
 * A quick "pop" for messages, a brighter double note for mentions and DMs, a rising/falling
 * pair when someone joins/leaves a call, and a gentle ring for incoming calls.
 */
const patterns: Record<AlertSound, [number, number, number, number][]> = {
  message: [
    [740, 0, 0.07, 0.07],
    [988, 0.06, 0.12, 0.06],
  ],
  mention: [
    [880, 0, 0.08, 0.1],
    [1319, 0.09, 0.16, 0.09],
  ],
  join: [
    [523, 0, 0.09, 0.07],
    [784, 0.08, 0.14, 0.07],
  ],
  leave: [
    [784, 0, 0.09, 0.06],
    [523, 0.08, 0.16, 0.06],
  ],
  call: [0, 1.2, 2.4].flatMap((ring) => [
    [659, ring, 0.2, 0.09] as [number, number, number, number],
    [880, ring + 0.22, 0.32, 0.09] as [number, number, number, number],
  ]),
};

/** Minimum gap between two sounds of the same kind: bursts of messages play once. */
const minGapMs: Record<AlertSound, number> = {
  message: 2500,
  mention: 1200,
  join: 800,
  leave: 800,
  call: 4000,
};
const lastPlayed = new Map<AlertSound, number>();

export function playSound(sound: AlertSound, force = false): void {
  if (!force && (!prefs.sound || quietNow())) return;
  const nowMs = Date.now();
  if (!force && nowMs - (lastPlayed.get(sound) ?? 0) < minGapMs[sound]) return;
  lastPlayed.set(sound, nowMs);
  try {
    audio ??= new AudioContext();
    // Browsers keep the context suspended until a user gesture has happened on the page.
    if (audio.state === 'suspended') void audio.resume();
    const now = audio.currentTime;
    for (const [frequency, start, duration, volume] of patterns[sound]) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = 'sine';
      osc.frequency.value = frequency;
      gain.gain.setValueAtTime(0, now + start);
      gain.gain.linearRampToValueAtTime(volume, now + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
      osc.connect(gain).connect(audio.destination);
      osc.start(now + start);
      osc.stop(now + start + duration + 0.05);
    }
  } catch {
    // No audio device / blocked: alerts are a nicety.
  }
}

/* ---------- System notifications ---------- */

export type PermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

export async function notificationPermission(): Promise<PermissionState> {
  if (isDesktop) {
    const { isPermissionGranted } = await import('@tauri-apps/plugin-notification');
    return (await isPermissionGranted()) ? 'granted' : 'default';
  }
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<PermissionState> {
  if (isDesktop) {
    const { requestPermission } = await import('@tauri-apps/plugin-notification');
    return (await requestPermission()) === 'granted' ? 'granted' : 'denied';
  }
  if (!('Notification' in window)) return 'unsupported';
  return Notification.requestPermission();
}

/** Shows a system notification when the app is not in the foreground. */
export async function notify(options: {
  title: string;
  body?: string;
  tag?: string;
  onClick?: () => void;
}): Promise<void> {
  if (!prefs.desktop || quietNow()) return;
  if (document.visibilityState === 'visible' && document.hasFocus()) return;
  try {
    if (isDesktop) {
      const { isPermissionGranted, sendNotification } =
        await import('@tauri-apps/plugin-notification');
      if (await isPermissionGranted())
        sendNotification({ title: options.title, body: options.body });
      return;
    }
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const notification = new Notification(options.title, {
      body: options.body,
      tag: options.tag,
      icon: '/favicon.svg',
    });
    notification.onclick = () => {
      window.focus();
      options.onClick?.();
      notification.close();
    };
  } catch {
    // Notifications unavailable (e.g. insecure context): sounds and in-app badges still work.
  }
}
